/* ============================================================
   Kenyan import duty and levy model.

   Order of assessment (KRA / EAC CMA 2004):

     Customs Value (CIF)
     + Import Duty   = CIF × CET rate
     + Excise Duty   = (CIF + Import Duty) × excise rate
     ────────────────────────────────────────────────
     VAT base        = CIF + Import Duty + Excise Duty
     + VAT           = VAT base × 16%
     + IDF           = CIF × 2.5%
     + RDL           = CIF × 2%
     ────────────────────────────────────────────────
     Total payable to KRA

   The one assumption worth flagging: IDF and RDL are treated as levies
   OUTSIDE the VAT base. That follows KRA's published order of
   assessment, but entries do vary by commodity and by how an agent
   lodges them. The tool states the assumption on screen and lets the
   user switch it, rather than hiding a judgement call inside a number.
   ============================================================ */

/** Anything that is not a usable number becomes the fallback. */
function toFinite(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback;
}

/** A rate is a proportion. Outside 0–1 it is a typo, not a position. */
function clampRate(rate) {
  return Math.min(1, Math.max(0, toFinite(rate)));
}

export const VAT_RATE = 0.16;
export const IDF_RATE = 0.025;
export const RDL_RATE = 0.02;

/** EAC Common External Tariff bands. */
export const DUTY_BANDS = [
  {
    id: 'zero',
    rate: 0,
    label: '0% — raw materials & capital goods',
    examples: 'Plant, machinery, pharmaceutical inputs, most raw materials'
  },
  {
    id: 'intermediate',
    rate: 0.1,
    label: '10% — intermediate goods',
    examples: 'Semi-processed inputs, some industrial supplies'
  },
  {
    id: 'finished',
    rate: 0.25,
    label: '25% — finished goods',
    examples: 'Most consumer goods, finished manufactures'
  },
  {
    id: 'sensitive',
    rate: 0.35,
    label: '35% — sensitive & strategic goods',
    examples: 'Goods on the EAC sensitive list, certain agricultural products'
  }
];

export function dutyBand(id) {
  return DUTY_BANDS.find((b) => b.id === id) || DUTY_BANDS[0];
}

/**
 * @param {object} input
 *   cif            customs value
 *   dutyRate       0–1
 *   exciseRate     0–1
 *   vatRate        0–1
 *   levyOnVatBase  when true, IDF and RDL join the VAT base
 * @returns {{lines: Array, totalTax: number, landedBeforeCharges: number}}
 */
export function assessDuty({
  cif,
  dutyRate = 0,
  exciseRate = 0,
  vatRate = VAT_RATE,
  idfRate = IDF_RATE,
  rdlRate = RDL_RATE,
  levyOnVatBase = false
}) {
  // Negative money and negative rates are not states of the world. A
  // negative CIF previously produced a negative landed cost with every
  // tax line silently filtered out by the `amount > 0` guard below —
  // a plausible-looking answer to an impossible question.
  const value = Math.max(0, toFinite(cif));
  const duty = clampRate(dutyRate);
  const excises = clampRate(exciseRate);
  const vat = clampRate(vatRate);
  const idfR = clampRate(idfRate);
  const rdlR = clampRate(rdlRate);

  const importDuty = value * duty;
  const excise = (value + importDuty) * excises;
  const idf = value * idfR;
  const rdl = value * rdlR;

  const vatBase = value + importDuty + excise + (levyOnVatBase ? idf + rdl : 0);
  const vatDue = vatBase * vat;

  const lines = [
    { id: 'cif', label: 'Customs value (CIF)', amount: value, basis: 'Declared', tax: false },
    {
      id: 'duty',
      label: 'Import duty',
      amount: importDuty,
      basis: `${(duty * 100).toFixed(0)}% of CIF`,
      tax: true
    },
    {
      id: 'excise',
      label: 'Excise duty',
      amount: excise,
      basis: `${(excises * 100).toFixed(1)}% of CIF + duty`,
      tax: true
    },
    {
      id: 'vat',
      label: 'VAT',
      amount: vatDue,
      basis: `${(vat * 100).toFixed(0)}% of ${levyOnVatBase ? 'CIF + duty + excise + levies' : 'CIF + duty + excise'}`,
      tax: true
    },
    {
      id: 'idf',
      label: 'Import Declaration Fee',
      amount: idf,
      basis: `${(idfR * 100).toFixed(1)}% of CIF`,
      tax: true
    },
    {
      id: 'rdl',
      label: 'Railway Development Levy',
      amount: rdl,
      basis: `${(rdlR * 100).toFixed(1)}% of CIF`,
      tax: true
    }
  ].filter((line) => line.id === 'cif' || line.amount > 0);

  const totalTax = lines.filter((l) => l.tax).reduce((sum, l) => sum + l.amount, 0);

  return { lines, totalTax, landedBeforeCharges: value + totalTax, vatBase };
}

/* ============================================================
   Demurrage & detention
   ============================================================ */

/**
 * Tiered daily charges. Carriers and terminals escalate after the first
 * few chargeable days, so a flat rate understates real exposure — which
 * is exactly how a USD 2,500 invoice arrives unforecast.
 */
export const DEFAULT_TIERS = [
  { fromDay: 1, toDay: 3, rate: 45 },
  { fromDay: 4, toDay: 7, rate: 90 },
  { fromDay: 8, toDay: null, rate: 150 }
];

/**
 * @param {number} daysHeld     total days the box has been/will be held
 * @param {number} freeDays     contractual free period
 * @param {Array}  tiers        escalation schedule
 * @param {number} containers   number of boxes
 */
export function assessDemurrage({ daysHeld, freeDays, tiers = DEFAULT_TIERS, containers = 1 }) {
  // Clamp before arithmetic. These are physical quantities: there is no
  // such thing as negative free time or a box held for minus two days,
  // and the previous code let both through — freeDays of -10 against a
  // box held 2 days produced 12 chargeable days and USD 1,245 of
  // demurrage. That was reachable from the corridor run, which guards
  // distance, payload and cost but not these.
  //
  // Clamping rather than throwing, because a tool must never lose the
  // user's on-screen result (audit A-06), and rather than returning an
  // error shape, because zero free days IS the correct reading of
  // "negative free days" — it is not a guess about intent. The form
  // layer still rejects the input so the user is told, rather than
  // silently corrected.
  const held = Math.max(0, toFinite(daysHeld));
  const free = Math.max(0, toFinite(freeDays));
  const boxes = Math.max(1, Math.floor(toFinite(containers, 1)));

  const chargeableDays = Math.max(0, held - free);
  const breakdown = [];
  let total = 0;

  for (const tier of tiers) {
    const tierStart = tier.fromDay;
    const tierEnd = tier.toDay ?? Infinity;
    if (chargeableDays < tierStart) continue;

    const days = Math.min(chargeableDays, tierEnd) - tierStart + 1;
    if (days <= 0) continue;

    const amount = days * Math.max(0, toFinite(tier.rate)) * boxes;
    total += amount;
    breakdown.push({
      label: tier.toDay ? `Days ${tier.fromDay}–${tier.toDay}` : `Day ${tier.fromDay}+`,
      days,
      rate: tier.rate,
      amount
    });
  }

  return {
    chargeableDays,
    daysRemaining: Math.max(0, free - held),
    total,
    breakdown,
    perContainer: total / boxes
  };
}

/** What one more day costs from here — the number that changes behaviour. */
export function marginalDayCost({ daysHeld, freeDays, tiers = DEFAULT_TIERS, containers = 1 }) {
  const now = assessDemurrage({ daysHeld, freeDays, tiers, containers });
  const next = assessDemurrage({ daysHeld: daysHeld + 1, freeDays, tiers, containers });
  return next.total - now.total;
}
